import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';

interface MessageListener {
  name: string;
  callback: (data: any) => void;
}

function generateUuid(length = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  private authState = inject(AuthStateService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  private socket: WebSocket | null = null;
  private currentUuid = generateUuid();
  private connectingPromise: Promise<WebSocket | null> | null = null;
  private messageListeners: MessageListener[] = [
    { name: 'destroy', callback: (data) => this.onDestroy(data) }
  ];

  connect(): WebSocket | null {
    const token = this.validateToken();
    if (!token) return null;

    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.socket = new WebSocket(environment.wsBaseUrl);
      this.connectingPromise = new Promise(resolve => {
        this.socket!.onopen = () => {
          this.socket!.send(JSON.stringify({ token, next: this.currentUuid }));
          resolve(this.socket);
          this.connectingPromise = null;
        };
        this.socket!.onerror = (err) => {
          console.error('[SocketService] error:', err);
          resolve(null);
          this.connectingPromise = null;
        };
      });
      this.socket.onmessage = (event) => this.handleMessage(event);
    }
    return this.socket;
  }

  async updateSocketUUID(): Promise<string | null> {
    const token = this.validateToken();
    if (!token) return null;

    const newUuid = generateUuid();
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ token, current: this.currentUuid, next: newUuid }));
      this.currentUuid = newUuid;
      return this.currentUuid;
    }

    this.currentUuid = newUuid;
    this.connect();
    await this.waitUntilOpen();
    return this.currentUuid;
  }

  getSocket(): WebSocket | null { return this.socket; }
  getUuid(): string { return this.currentUuid; }

  onMessage(name: string, callback: (data: any) => void): void {
    if (!this.messageListeners.some(l => l.name === name)) {
      this.messageListeners.push({ name, callback });
    }
  }

  disconnect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
  }

  private handleMessage(event: MessageEvent): void {
    let data: any;
    try { data = JSON.parse(event.data); } catch { return; }
    const listener = this.messageListeners.find(l => l.name === data.state);
    if (listener) listener.callback(data);
  }

  private validateToken(): string | null {
    const token = this.authState.token;
    if (!token) { this.disconnect(); this.currentUuid = generateUuid(); return null; }
    return token;
  }

  private async waitUntilOpen(timeoutMs = 1500): Promise<WebSocket | null> {
    if (this.socket?.readyState === WebSocket.OPEN) return this.socket;
    if (this.connectingPromise) {
      return Promise.race([
        this.connectingPromise,
        new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs)),
      ]);
    }
    return this.connect();
  }

  private onDestroy(_data: any): void {
    this.authState.logout();
    this.toastr.success('ログアウトしました。', '', { timeOut: 5000 });
    this.socket?.close();
    this.router.navigate(['/login']);
  }
}
